import { useState, useCallback } from 'react';
import { uploadFile } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Upload, X, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FileUpload = ({ 
  onFilesChange, 
  maxFiles = 5, 
  acceptedTypes = 'image/*,.pdf,.zip,.md,.txt',
  existingFiles = [] 
}) => {
  const [files, setFiles] = useState(existingFiles);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    await handleFiles(droppedFiles);
  }, [files, maxFiles]);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await handleFiles(selectedFiles);
  };

  const handleFiles = async (newFiles) => {
    if (files.length + newFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setIsUploading(true);
    const uploadedFiles = [];

    for (const file of newFiles) {
      try {
        const response = await uploadFile(file);
        uploadedFiles.push({
          ...response.data,
          url: `${BACKEND_URL}/api/files/${response.data.file_id}`,
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    const updatedFiles = [...files, ...uploadedFiles];
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
    setIsUploading(false);

    if (uploadedFiles.length > 0) {
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const isImage = (contentType) => contentType?.startsWith('image/');

  return (
    <div className="space-y-4" data-testid="file-upload">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'drop-zone border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer',
          isDragging ? 'active border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-slate-400'
        )}
        onClick={() => document.getElementById('file-input').click()}
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            <p className="text-slate-600">Uploading...</p>
          </div>
        ) : (
          <>
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600 mb-1">
              Drag and drop files here, or click to browse
            </p>
            <p className="text-sm text-slate-500">
              Images, PDFs, ZIP files • Max {maxFiles} files
            </p>
          </>
        )}
        <input
          id="file-input"
          type="file"
          multiple
          accept={acceptedTypes}
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={file.file_id || index}
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
              data-testid={`uploaded-file-${index}`}
            >
              {isImage(file.content_type) ? (
                <div className="w-12 h-12 rounded overflow-hidden bg-slate-200 flex-shrink-0">
                  <img
                    src={file.url}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-slate-500" />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{file.filename}</p>
                <p className="text-xs text-slate-500">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(index)}
                className="text-slate-400 hover:text-red-500"
                data-testid={`remove-file-${index}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
